<template>
    <div>
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <h1 class="text-xl font-bold text-gray-900">Facturation</h1>
            <button
                v-if="addButton"
                @click="openModal(null)"
                class="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 active:scale-95 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all duration-150"
            >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                Nouveau document
            </button>
        </div>

        <!-- Filtres -->
        <div class="flex flex-wrap gap-2 mb-4">
            <div class="relative">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/></svg>
                <input v-model="search" type="text" placeholder="Rechercher…" class="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-52" @input="onSearchInput"/>
            </div>
            <select v-model="filters.document_type" class="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" @change="fetchInvoices()">
                <option value="">Devis & Factures</option>
                <option value="devis">Devis</option>
                <option value="facture">Factures</option>
            </select>
            <select v-model="filters.status" class="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" @change="fetchInvoices()">
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="issued">Document créé</option>
                <option value="sent">Envoyée</option>
                <option value="partial">Partiel</option>
                <option value="paid">Payée</option>
                <option value="cancelled">Annulée</option>
            </select>
            <button
                @click="toggleOverdue"
                :class="['inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border transition-all', filters.overdue ? 'bg-red-50 border-red-300 text-red-600' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300']"
            >
                <span :class="['w-2 h-2 rounded-full', filters.overdue ? 'bg-red-500' : 'bg-gray-300']"></span>
                En retard
                <span v-if="overdueCount !== null" :class="['text-xs font-bold px-1.5 py-0.5 rounded-full', filters.overdue ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500']">{{ overdueCount }}</span>
            </button>
        </div>

        <!-- Tableau -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table class="min-w-full text-sm">
                <thead>
                    <tr class="border-b border-gray-100 bg-gray-50">
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">N° / Client</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant TTC</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Échéance</th>
                        <th class="px-4 py-3 w-10"></th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">

                    <!-- Skeleton -->
                    <template v-if="loading">
                        <tr v-for="n in 8" :key="'sk-' + n">
                            <td class="px-4 py-3.5"><div class="h-3 bg-gray-100 rounded w-24 mb-1.5"></div><div class="h-2.5 bg-gray-100 rounded w-32"></div></td>
                            <td class="px-4 py-3.5"><div class="h-5 bg-gray-100 rounded-full w-16"></div></td>
                            <td class="px-4 py-3.5"><div class="h-5 bg-gray-100 rounded-full w-20"></div></td>
                            <td class="px-4 py-3.5 text-right"><div class="h-3 bg-gray-100 rounded w-16 ml-auto"></div></td>
                            <td class="px-4 py-3.5"><div class="h-3 bg-gray-100 rounded w-20"></div></td>
                            <td class="px-4 py-3.5"><div class="h-6 bg-gray-100 rounded w-6 ml-auto"></div></td>
                        </tr>
                    </template>

                    <!-- Vide -->
                    <tr v-else-if="invoices.length === 0">
                        <td colspan="6" class="px-4 py-14 text-center">
                            <p class="text-sm text-gray-400 font-medium">Aucun document trouvé</p>
                            <p class="text-xs text-gray-300 mt-1">Modifiez vos filtres ou créez un nouveau document</p>
                        </td>
                    </tr>

                    <!-- Lignes -->
                    <tr v-else v-for="invoice in invoices" :key="invoice.id" class="hover:bg-gray-50 transition-colors relative" :class="loadingInvoiceId === invoice.id ? 'opacity-50 pointer-events-none' : ''">

                        <!-- Overlay loader sur la ligne -->
                        <td v-if="loadingInvoiceId === invoice.id"
                            colspan="6"
                            class="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                            <svg class="w-4 h-4 animate-spin text-slate-900" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                        </td>

                        <td class="px-4 py-3.5">
                            <p class="font-semibold text-gray-800">{{ invoice.invoice_number || invoice.num_invoice || '—' }}</p>
                            <p class="text-xs text-gray-400 mt-0.5">{{ clientName(invoice) }}</p>
                        </td>
                        <td class="px-4 py-3.5">
                            <span :class="typeClass(invoice.document_type)" class="text-xs font-semibold px-2 py-1 rounded-full">
                                {{ invoice.document_type === 'facture' ? 'Facture' : invoice.document_type === 'avoir' ? 'Avoir' : 'Devis' }}
                            </span>
                        </td>
                        <td class="px-4 py-3.5">
                            <span :class="statusClass(invoice.status)" class="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full">
                                <span class="w-1.5 h-1.5 rounded-full" :class="statusDot(invoice.status)"></span>
                                {{ statusLabel(invoice.status) }}
                            </span>
                            <span v-if="hasPartialPayment(invoice)" class="block text-xs text-orange-500 mt-0.5">
                                Reste {{ formatAmount(invoice.solde_montant) }}
                            </span>
                        </td>
                        <td class="px-4 py-3.5 text-right">
                            <p class="font-bold text-gray-800">{{ formatAmount(invoice.total_ttc) }}</p>
                            <p v-if="invoice.status === 'partial'" class="text-xs text-orange-500">
                                Payé {{ formatAmount(invoice.acompte_montant) }}
                            </p>
                        </td>
                        <td class="px-4 py-3.5">
                            <span v-if="invoice.date_limite_paiement" :class="isOverdue(invoice) ? 'text-red-500 font-semibold' : 'text-gray-400'" class="text-xs">
                                <span v-if="isOverdue(invoice)">⚠ </span>{{ formatDate(invoice.date_limite_paiement) }}
                            </span>
                            <span v-else class="text-xs text-gray-300">—</span>
                        </td>

                        <!-- Dropdown trigger -->
                        <td class="px-4 py-3.5 text-right">
                            <button
                                @click.stop="toggleDropdown(invoice, $event)"
                                :class="['w-7 h-7 flex items-center justify-center rounded-lg transition-colors', openDropdown && openDropdown.id === invoice.id ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600']"
                            >
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                                </svg>
                            </button>
                        </td>
                    </tr>

                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.last_page > 1" class="flex items-center justify-between mt-4">
            <p class="text-xs text-gray-400">{{ pagination.from }}–{{ pagination.to }} sur {{ pagination.total }}</p>
            <div class="flex gap-1">
                <button :disabled="pagination.current_page === 1" @click="goToPage(pagination.current_page - 1)" class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <button v-for="page in visiblePages" :key="page" :disabled="page === '...'" :class="['w-8 h-8 rounded-lg text-sm font-medium transition-colors', page === pagination.current_page ? 'bg-blue-600 text-white' : page === '...' ? 'text-gray-300 cursor-default' : 'border border-gray-200 text-gray-600 hover:bg-gray-50']" @click="page !== '...' && goToPage(page)">{{ page }}</button>
                <button :disabled="pagination.current_page === pagination.last_page" @click="goToPage(pagination.current_page + 1)" class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
            </div>
        </div>

        <!-- ======================== -->
        <!-- MODALE : CRÉER/MODIFIER  -->
        <!-- ======================== -->
        <modal
            name="invoice-form"
            :scrollable="false"
            height="90%"
            :adaptive="true"
            :clickToClose="false"
            width="1200px"
            classes="!rounded-2xl overflow-hidden"
        >
            <div class="h-full flex flex-col">
                <!-- Header -->
                <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <h2 class="text-base font-bold text-gray-900">
                        {{ selectedInvoice ? 'Modifier le document' : 'Nouveau document' }}
                    </h2>
                    <button @click="closeModal" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <!-- Wizard (gère son propre scroll en interne) -->
                <facture-wizard
                    :key="selectedInvoice ? selectedInvoice.id : 'new'"
                    class="flex-1 min-h-0"
                    :invoice-id="selectedInvoice ? selectedInvoice.id : null"
                    :chantier-id="selectedInvoice ? selectedInvoice.chantier_id : (chantierId || null)"
                    :client-id="selectedInvoice ? (selectedInvoice.client_id || selectedInvoice.client?.id) : (clientId || null)"
                    :adresse-id="selectedInvoice ? (selectedInvoice.adresse_id || selectedInvoice.client?.adresses?.[0]?.id) : (adresseId || null)"
                    @invoice-created="onInvoiceCreated"
                />
            </div>
        </modal>

        <!-- ======================== -->
        <!-- MODALE : EMAIL           -->
        <!-- ======================== -->
        <modal name="send-email" height="auto" :scrollable="true" :clickToClose="false" :adaptive="true" width="600">
            <div class="p-6" v-if="emailForm">
                <div class="flex items-center justify-between mb-5">
                    <div>
                        <h2 class="text-lg font-bold text-gray-900">Envoyer par email</h2>
                        <p class="text-sm text-gray-400 mt-0.5">{{ emailInvoice && (emailInvoice.invoice_number || emailInvoice.num_invoice) }}</p>
                    </div>
                    <button @click="$modal.hide('send-email')" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
                <div class="space-y-4">
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">À</label>
                        <input v-model="emailForm.to" type="email" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="email@client.fr"/>
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Objet</label>
                        <input v-model="emailForm.subject" type="text" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
                        <textarea v-model="emailForm.message" rows="8" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none leading-relaxed"></textarea>
                    </div>
                    <label class="flex items-center gap-3 cursor-pointer select-none">
                        <div :class="['w-10 h-5 rounded-full transition-colors relative flex-shrink-0', emailForm.attach ? 'bg-blue-500' : 'bg-gray-200']" @click="emailForm.attach = !emailForm.attach">
                            <span :class="['absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform', emailForm.attach ? 'translate-x-5' : 'translate-x-0.5']"></span>
                        </div>
                        <span class="text-sm text-gray-700">Joindre le document en PDF</span>
                    </label>
                </div>
                <div class="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">
                    <button @click="$modal.hide('send-email')" class="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors">Annuler</button>
                    <button @click="sendEmail" :disabled="submitting || !emailForm.to" class="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all active:scale-95">
                        <svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                        {{ submitting ? 'Envoi…' : 'Envoyer' }}
                    </button>
                </div>
            </div>
        </modal>

        <!-- ======================== -->
        <!-- MODALE : PAIEMENT        -->
        <!-- ======================== -->
        <modal name="payment-modal" height="auto" :scrollable="true" :clickToClose="false" :adaptive="true" width="480">
            <div class="p-6" v-if="paymentInvoice">
                <div class="flex items-center justify-between mb-5">
                    <h2 class="text-lg font-bold text-gray-900">Enregistrer un paiement</h2>
                    <button @click="$modal.hide('payment-modal')" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>
                <div class="bg-gray-50 rounded-xl p-4 mb-5 space-y-2 text-sm">
                    <div class="flex justify-between text-gray-600">
                        <span>{{ paymentInvoice.invoice_number || paymentInvoice.num_invoice }}</span>
                        <span class="font-bold text-gray-800">{{ formatAmount(paymentInvoice.total_ttc) }}</span>
                    </div>
                    <div v-if="paymentInvoice.acompte_montant > 0" class="flex justify-between text-orange-500">
                        <span>Déjà réglé</span>
                        <span class="font-medium">{{ formatAmount(paymentInvoice.acompte_montant) }}</span>
                    </div>
                    <div class="flex justify-between text-gray-800 font-semibold pt-1 border-t border-gray-200">
                        <span>Reste à payer</span>
                        <span>{{ formatAmount(remainingAmount) }}</span>
                    </div>
                </div>
                <div class="mb-4">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Type de règlement</label>
                    <div class="grid grid-cols-3 gap-2">
                        <button v-for="type in paymentTypes" :key="type.value" @click="setPaymentType(type.value)" :class="['flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 text-xs font-semibold transition-all', paymentForm.type === type.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300']">
                            <i class="text-lg" :class="type.icon"></i>
                            <span class="text-xs font-light">{{ type.label }}</span>
                        </button>
                    </div>
                </div>
                <div class="space-y-4">
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Montant (€)</label>
                        <input v-model.number="paymentForm.amount" type="number" step="0.01" min="0" :max="remainingAmount" :readonly="paymentForm.type === 'full'" :class="['w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500', paymentForm.type === 'full' ? 'bg-gray-50 text-gray-500' : '']"/>
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Date</label>
                            <input v-model="paymentForm.date" type="date" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                        </div>
                        <div>
                            <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Méthode</label>
                            <select v-model="paymentForm.method" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option value="virement">Virement</option>
                                <option value="cheque">Chèque</option>
                                <option value="especes">Espèces</option>
                                <option value="cb">Carte bancaire</option>
                                <option value="prelevement">Prélèvement</option>
                                <option value="autre">Autre</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Référence <span class="text-gray-300 font-normal normal-case">(optionnel)</span></label>
                        <input v-model="paymentForm.reference" type="text" placeholder="N° chèque, référence virement…" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    </div>
                    <div>
                        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Note <span class="text-gray-300 font-normal normal-case">(optionnel)</span></label>
                        <textarea v-model="paymentForm.note" rows="2" class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"></textarea>
                    </div>
                </div>
                <div class="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">
                    <button @click="$modal.hide('payment-modal')" class="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors">Annuler</button>
                    <button @click="submitPayment" :disabled="submitting || !paymentForm.amount" class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all active:scale-95">
                        <svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        {{ submitting ? 'Enregistrement…' : 'Enregistrer' }}
                    </button>
                </div>
            </div>
        </modal>

        <!-- ============================ -->
        <!-- MODALE : HISTORIQUE PAIEMENTS -->
        <!-- ============================ -->
        <modal name="payment-history" height="auto" :scrollable="true" :adaptive="true" width="520">
            <div class="p-6" v-if="historyInvoice">

                <!-- Header -->
                <div class="flex items-start justify-between mb-6">
                    <div>
                        <h2 class="text-lg font-bold text-gray-900">Paiements</h2>
                        <p class="text-sm text-gray-400 mt-0.5">{{ historyInvoice.invoice_number || historyInvoice.num_invoice }}</p>
                    </div>
                    <button @click="$modal.hide('payment-history')" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                </div>

                <!-- Recap chiffres -->
                <div class="grid grid-cols-3 gap-3 mb-6">
                    <div class="bg-gray-50 rounded-xl p-3 text-center">
                        <p class="text-xs text-gray-400 mb-1">Total facture</p>
                        <p class="text-sm font-bold text-gray-800">{{ formatAmount(historyInvoice.total_ttc) }}</p>
                    </div>
                    <div class="bg-green-50 rounded-xl p-3 text-center">
                        <p class="text-xs text-green-500 mb-1">Réglé</p>
                        <p class="text-sm font-bold text-green-700">{{ formatAmount(historyData.total_paid || historyInvoice.acompte_montant) }}</p>
                    </div>
                    <div :class="['rounded-xl p-3 text-center', historyData.remaining > 0 ? 'bg-orange-50' : 'bg-green-50']">
                        <p :class="['text-xs mb-1', historyData.remaining > 0 ? 'text-orange-400' : 'text-green-500']">Reste</p>
                        <p :class="['text-sm font-bold', historyData.remaining > 0 ? 'text-orange-600' : 'text-green-700']">{{ formatAmount(historyData.remaining || 0) }}</p>
                    </div>
                </div>

                <!-- Barre de progression -->
                <div class="mb-6">
                    <div class="flex justify-between text-xs text-gray-400 mb-1.5">
                        <span>Progression du paiement</span>
                        <span class="font-semibold" :class="historyData.percent_paid >= 100 ? 'text-green-600' : 'text-gray-600'">{{ historyData.percent_paid || 0 }}%</span>
                    </div>
                    <div class="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            class="h-full rounded-full transition-all duration-500"
                            :class="historyData.percent_paid >= 100 ? 'bg-green-500' : 'bg-blue-500'"
                            :style="{ width: (historyData.percent_paid || 0) + '%' }"
                        ></div>
                    </div>
                </div>

                <!-- Liste des paiements -->
                <div class="mb-2">
                    <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        {{ paymentHistory.length }} règlement{{ paymentHistory.length > 1 ? 's' : '' }}
                    </p>

                    <div v-if="paymentHistory.length === 0" class="py-8 text-center">
                        <p class="text-sm text-gray-400">Aucun paiement enregistré</p>
                    </div>

                    <div v-else class="space-y-2">
                        <div
                            v-for="(payment, i) in paymentHistory"
                            :key="payment.id || i"
                            class="flex items-center justify-between p-3.5 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                        >
                            <div class="flex items-center gap-3">
                                <!-- Icône méthode -->
                                <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" :class="methodBg(payment.method)">
                                    <span class="text-base">{{ methodIcon(payment.method) }}</span>
                                </div>
                                <div>
                                    <div class="flex items-center gap-2">
                                        <p class="text-sm font-semibold text-gray-800">{{ formatAmount(payment.amount) }}</p>
                                        <span :class="paymentTypeBadge(payment.type)" class="text-xs font-medium px-1.5 py-0.5 rounded-md">{{ paymentTypeLabel(payment.type) }}</span>
                                    </div>
                                    <div class="flex items-center gap-2 mt-0.5">
                                        <p class="text-xs text-gray-400">{{ formatDate(payment.date) }}</p>
                                        <span class="text-gray-200">·</span>
                                        <p class="text-xs text-gray-400">{{ methodLabel(payment.method) }}</p>
                                        <template v-if="payment.reference">
                                            <span class="text-gray-200">·</span>
                                            <p class="text-xs text-gray-400">{{ payment.reference }}</p>
                                        </template>
                                    </div>
                                    <p v-if="payment.note" class="text-xs text-gray-400 italic mt-0.5">{{ payment.note }}</p>
                                </div>
                            </div>

                            <!-- Supprimer si pas synced -->
                            <button
                                v-if="!payment.external_id"
                                @click="deletePayment(historyInvoice, payment)"
                                class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 ml-2"
                                title="Supprimer ce paiement"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0 1 16.138 21H7.862a2 2 0 0 1-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v3M4 7h16"/></svg>
                            </button>
                            <span v-else class="text-xs text-gray-300 ml-2 flex-shrink-0" title="Synchronisé avec votre logiciel de facturation">
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 0 0 4.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 0 1-15.357-2m15.357 2H15"/></svg>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Ajouter un paiement depuis l'historique -->
                <div v-if="historyData.remaining > 0.01" class="mt-5 pt-4 border-t border-gray-100">
                    <button
                        @click="$modal.hide('payment-history'); openPaymentModal(historyInvoice)"
                        class="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-400 hover:text-blue-600 text-sm font-medium py-2.5 rounded-xl transition-all"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                        Ajouter un paiement
                    </button>
                </div>

            </div>
        </modal>

        <!-- ======================== -->
        <!-- MODALE : SUPPRESSION     -->
        <!-- ======================== -->
        <modal name="delete-confirm" height="auto" :adaptive="true" width="400">
            <div class="p-6">
                <div class="flex items-start gap-4 mb-5">
                    <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
                    </div>
                    <div>
                        <h3 class="text-base font-bold text-gray-900">Supprimer ce document ?</h3>
                        <p class="text-sm text-gray-500 mt-1">
                            <span class="font-medium text-gray-700">{{ invoiceToDelete && (invoiceToDelete.invoice_number || invoiceToDelete.num_invoice) }}</span> sera supprimé définitivement.
                        </p>
                    </div>
                </div>
                <div class="flex gap-3">
                    <button @click="$modal.hide('delete-confirm')" class="flex-1 text-sm font-medium text-gray-600 border border-gray-200 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Annuler</button>
                    <button @click="deleteInvoice" :disabled="submitting" class="flex-1 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 py-2.5 rounded-xl transition-colors disabled:opacity-50">
                        {{ submitting ? 'Suppression…' : 'Supprimer' }}
                    </button>
                </div>
            </div>
        </modal>

        <!-- ======================== -->
        <!-- DROPDOWN                 -->
        <!-- ======================== -->
        <div to="body">
            <div v-if="openDropdown" class="fixed z-50" :style="{ top: dropdownPos.top + 'px', left: dropdownPos.left + 'px' }">
                <div ref="dropdownMenu" class="bg-white border border-gray-200 rounded-xl shadow-xl py-1 w-72" @click.stop>
                    <!-- Émettre (brouillon uniquement) — AJOUTER ce bouton avant "Modifier" -->
                    <button
                        v-if="openDropdown.status === 'draft'"
                        @click="markAsDone(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                        Créer le document
                    </button>

                    <!-- Voir le document -->
                    <a
                        v-if="openDropdown.document_url"
                        :href="openDropdown.document_url"
                        target="_blank"
                        class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        @click="closeDropdown"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                        Voir le document
                    </a>

                    <!-- Modifier (draft) -->
                    <button
                        v-if="openDropdown.status === 'draft'"
                        @click="openModal(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        Modifier
                    </button>

                    <!-- Envoyer par email -->
                    <button
                        v-if="!['draft','cancelled'].includes(openDropdown.status)"
                        @click="openEmailModal(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"/></svg>
                        Envoyer par email
                    </button>

                    <!-- Marquer comme envoyée -->
                    <button
                        v-if="openDropdown.status === 'issued'"
                        @click="markAsSent(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                        Marquer comme envoyée
                    </button>

                    <!-- Marquer comme accepté (devis) -->
                    <button
                        v-if="openDropdown.document_type === 'devis' && !['cancelled','accepted','rejected','draft'].includes(openDropdown.status)"
                        @click="changeStatus(openDropdown, 'accepted'); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                        Marquer comme accepté
                    </button>

                    <!-- Marquer comme refusé (devis) -->
                    <button
                        v-if="openDropdown.document_type === 'devis' && !['cancelled','rejected','draft'].includes(openDropdown.status)"
                        @click="changeStatus(openDropdown, 'refused'); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                        Marquer comme refusé
                    </button>

                    <!-- Enregistrer un paiement -->
                    <button
                        v-if="!['draft','cancelled','devis'].includes(openDropdown.status) && openDropdown.document_type !== 'devis'"
                        @click="openPaymentModal(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                        Enregistrer un paiement
                    </button>

                    <!-- Historique des paiements (si y a des paiements) -->
                    <button
                        v-if="!['draft','cancelled'].includes(openDropdown.status) && openDropdown.document_type !== 'devis'"
                        @click="openPaymentHistory(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 0 2-2h2a2 2 0 0 0 2 2"/></svg>
                        Historique des paiements
                    </button>

                    <!-- Convertir en facture -->
                    <button
                        v-if="openDropdown.document_type === 'devis' && !['cancelled','paid','draft'].includes(openDropdown.status)"
                        @click="convertToInvoice(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m-6 9l2 2 4-4"/></svg>
                        Convertir en facture
                    </button>

                    <!-- Dupliquer -->
                    <button
                        @click="duplicateInvoice(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z"/></svg>
                        Dupliquer
                    </button>

                    <!-- Créer un avoir -->
                    <!-- <button
                        v-if="openDropdown.status === 'paid' && openDropdown.document_type === 'facture'"
                        @click="createAvoir(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6m-6-6l6-6"/></svg>
                        Créer un avoir
                    </button> -->

                    <!-- Séparateur -->
                    <div v-if="['draft','issued','sent'].includes(openDropdown.status)" class="border-t border-gray-100 my-1"></div>

                    <!-- Supprimer (draft) -->
                    <button
                        v-if="openDropdown.status === 'draft'"
                        @click="confirmDelete(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0 1 16.138 21H7.862a2 2 0 0 1-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v3M4 7h16"/></svg>
                        Supprimer
                    </button>

                    <!-- Annuler -->
                    <button
                        v-if="['issued','sent'].includes(openDropdown.status)"
                        @click="cancelInvoice(openDropdown); closeDropdown()"
                        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"/></svg>
                        Annuler le document
                    </button>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FactureWizard from './Components/FactureWizard.vue';

export default {
    components: {
        FactureWizard,
    },
    props: {
        chantierId: { type: [Number, String], default: null },
        clientId:   { type: [Number, String], default: null },
        adresseId:  { type: [Number, String], default: null },
        addButton: { type: Boolean, default: true },
    },
    data() {
        return {
            invoices: [],
            pagination: null,
            loading: false,
            submitting: false,
            search: '',
            searchTimeout: null,
            overdueCount: null,

            selectedInvoice: null,
            invoiceToDelete: null,

            // Dropdown
            openDropdown: null,
            dropdownPos: { top: 0, left: 0 },

            // Email
            emailInvoice: null,
            emailForm: null,

            // Paiement
            paymentInvoice: null,
            paymentForm: this.defaultPaymentForm(),
            paymentTypes: [
                { value: 'full',    label: 'Complet', icon: 'fas fa-check' },
                { value: 'partial', label: 'Partiel', icon: 'fas fa-money-bill-wave' },
                { value: 'deposit', label: 'Acompte', icon: 'fas fa-file-invoice' },
            ],

            // Historique
            historyInvoice: null,
            historyData: {},
            paymentHistory: [],

            filters: {
                status: '',
                document_type: '',
                overdue: false,
            },

            loadingStatusChange: null,
            loadingInvoiceId: null,
        }
    },

    computed: {
        visiblePages() {
            if (!this.pagination) return []
            const { current_page, last_page } = this.pagination
            const pages = []
            for (let i = 1; i <= last_page; i++) {
                if (i === 1 || i === last_page || Math.abs(i - current_page) <= 1) pages.push(i)
                else if (pages[pages.length - 1] !== '...') pages.push('...')
            }
            return pages
        },

        remainingAmount() {
            if (!this.paymentInvoice) return 0
            return this.paymentInvoice.total_ttc - (this.paymentInvoice.acompte_montant || 0)
        },
    },

    mounted() {
        this.fetchInvoices()
        this.fetchOverdueCount()
        document.addEventListener('click', this.closeDropdown)
        document.addEventListener('scroll', this.closeDropdown, true)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.closeDropdown)
        document.removeEventListener('scroll', this.closeDropdown, true)
    },

    methods: {
        defaultPaymentForm() {
            return {
                type: 'full',
                amount: 0,
                date: new Date().toISOString().split('T')[0],
                method: 'virement',
                reference: '',
                note: '',
            }
        },

        // ─── Fetch ───────────────────────────────
        async fetchInvoices(page = 1) {
            this.loading = true
            try {
                const response = await this.$request.post('/factures/list', {
                    search:        this.search,
                    status:        this.filters.status,
                    document_type: this.filters.document_type,
                    overdue:       this.filters.overdue ? 1 : 0,
                    chantier_id:   this.chantierId  || null,   // ← nouveau
                    client_id:     this.clientId    || null,   // ← nouveau
                    page,
                    per_page: 25,
                })
                this.invoices   = response.data
                this.pagination = {
                    current_page: response.current_page,
                    last_page:    response.last_page,
                    from:         response.from,
                    to:           response.to,
                    total:        response.total,
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.loading = false
            }
        },

        async fetchOverdueCount() {
            try {
                const r = await this.$request.post('/factures/list', { overdue: 1, per_page: 1 })
                this.overdueCount = r.total
            } catch (e) {}
        },

        onSearchInput() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => this.fetchInvoices(), 350)
        },

        goToPage(page)   { this.fetchInvoices(page) },
        toggleOverdue()  { this.filters.overdue = !this.filters.overdue; this.fetchInvoices() },

        // ─── Dropdown (viewport-aware) ────────────
        toggleDropdown(invoice, event) {
            if (this.openDropdown && this.openDropdown.id === invoice.id) {
                this.openDropdown = null
                return
            }

            const rect     = event.currentTarget.getBoundingClientRect()
            const menuWidth = 288 // w-72

            let left = rect.right - menuWidth
            if (left < 8) left = 8

            // 1. Ouvrir invisible en dessous pour mesurer la vraie hauteur
            this.dropdownPos  = { top: rect.bottom + 6, left, ready: false }
            this.openDropdown = invoice

            // 2. Après le rendu, mesurer et repositionner si nécessaire
            this.$nextTick(() => {
                const menu = this.$refs.dropdownMenu
                if (!menu) return

                const menuHeight = menu.offsetHeight
                let top = rect.bottom + 6

                if (top + menuHeight > window.innerHeight - 8) {
                    // Afficher au-dessus, collé sous le haut du bouton
                    top = rect.top - menuHeight - 6
                }

                if (top < 8) top = 8

                this.dropdownPos = { top, left, ready: true }
            })
        },


        closeDropdown() {
            this.openDropdown = null
        },

        // ─── Modale create/edit ───────────────────
        openModal(invoice) {
            this.selectedInvoice = invoice
            this.$modal.show('invoice-form')
        },
        closeModal() {
            this.$modal.hide('invoice-form')
            this.selectedInvoice = null
        },

        // ─── Actions ─────────────────────────────
        async markAsSent(invoice) {
            try {
                await this.$request.post(`/factures/mark-sent/${invoice.id}`)
                this.fetchInvoices()
            } catch (e) { console.error(e) }
        },

        async duplicateInvoice(invoice) {
            await this.withLoading(invoice.id, async () => {
                await this.$request.post(`/factures/duplicate/${invoice.id}`)
                this.fetchInvoices()
            })
        },

        async convertToInvoice(invoice) {
            await this.withLoading(invoice.id, async () => {
                await this.$request.post(`/factures/convert-devis`, { invoice_id: invoice.id })
                this.fetchInvoices()
            })
        },

        async createAvoir(invoice) {
            try {
                await this.$request.post(`/factures/avoir/${invoice.id}`)
                this.fetchInvoices()
            } catch (e) { console.error(e) }
        },

        async cancelInvoice(invoice) {
            if (!confirm(`Annuler ${invoice.invoice_number || invoice.num_invoice} ?`)) return
            await this.withLoading(invoice.id, async () => {
                await this.$request.post(`/factures/cancel/${invoice.id}`)
                this.fetchInvoices()
            })
        },

        // ─── Email ───────────────────────────────
        openEmailModal(invoice) {
            this.emailInvoice = invoice
            const client    = invoice.client || invoice.chantier?.client
            const typeLabel = invoice.document_type === 'facture' ? 'Facture' : 'Devis'
            const num       = invoice.invoice_number || invoice.num_invoice
            const montant   = this.formatAmount(invoice.total_ttc)
            const echeance  = invoice.date_limite_paiement
                ? `Le règlement est à effectuer avant le ${this.formatDate(invoice.date_limite_paiement)}.`
                : ''

            this.emailForm = {
                to:      client?.email || '',
                cc:      '',
                subject: `${typeLabel} n° ${num}`,
                attach:  true,
                message: `Bonjour ${client?.prenom || ''},\n\nVeuillez trouver ci-joint votre ${typeLabel.toLowerCase()} n° ${num} d'un montant de ${montant} TTC.\n\n${echeance}\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement`,
            }
            this.$modal.show('send-email')
        },

        async sendEmail() {
            this.submitting = true
            try {
                await this.$request.post(`/factures/send-email/${this.emailInvoice.id}`, this.emailForm)
                this.$modal.hide('send-email')
                this.fetchInvoices()
            } catch (e) {
                console.error(e)
            } finally {
                this.submitting = false
            }
        },

        // ─── Paiement ────────────────────────────
        openPaymentModal(invoice) {
            this.paymentInvoice = invoice
            const remaining = invoice.total_ttc - (invoice.acompte_montant || 0)
            this.paymentForm = { ...this.defaultPaymentForm(), amount: remaining }
            this.$modal.show('payment-modal')
        },

        setPaymentType(type) {
            this.paymentForm.type = type
            if (type === 'full')    this.paymentForm.amount = this.remainingAmount
            if (type === 'deposit') this.paymentForm.amount = Math.round(this.paymentInvoice.total_ttc * 0.3 * 100) / 100
        },

        async submitPayment() {
            this.submitting = true
            try {
                await this.$request.post(`/factures/payment/${this.paymentInvoice.id}`, this.paymentForm)
                this.$modal.hide('payment-modal')
                this.fetchInvoices()
                this.fetchOverdueCount()
            } catch (e) {
                console.error(e)
            } finally {
                this.submitting = false
            }
        },

        // ─── Historique ──────────────────────────
        async openPaymentHistory(invoice) {
            this.historyInvoice  = invoice
            this.historyData     = {}
            this.paymentHistory  = []
            this.$modal.show('payment-history')
            try {
                const r = await this.$request.post(`/factures/payments/${invoice.id}`)
                this.paymentHistory = r.payments || []
                this.historyData    = {
                    total_paid:   r.total_paid,
                    remaining:    r.remaining,
                    percent_paid: r.percent_paid,
                }
            } catch (e) { console.error(e) }
        },

        async deletePayment(invoice, payment) {
            if (!confirm('Supprimer ce paiement ?')) return
            try {
                await this.$request.post(`/factures/payment/${invoice.id}/delete/${payment.id}`)
                await this.openPaymentHistory(invoice)
                this.fetchInvoices()
            } catch (e) { console.error(e) }
        },

        // ─── Suppression ─────────────────────────
        confirmDelete(invoice) {
            this.invoiceToDelete = invoice
            this.$modal.show('delete-confirm')
        },

        async deleteInvoice() {
            if (!this.invoiceToDelete) return
            this.submitting = true
            try {
                await this.$request.post(`/factures/delete/${this.invoiceToDelete.id}`)
                this.$modal.hide('delete-confirm')
                this.invoiceToDelete = null
                this.fetchInvoices()
                this.fetchOverdueCount()
            } catch (e) {
                console.error(e)
            } finally {
                this.submitting = false
            }
        },

        // ─── Helpers ─────────────────────────────
        clientName(invoice) {
            const c = invoice.client || invoice.chantier?.client
            return c ? [c.prenom, c.nom].filter(Boolean).join(' ') : '—'
        },

        hasPartialPayment(invoice) {
            return invoice.status === 'partial'
        },

        formatAmount(value) {
            if (value == null) return '—'
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value)
        },

        formatDate(value) {
            if (!value) return '—'
            return new Date(value).toLocaleDateString('fr-FR')
        },

        isOverdue(invoice) {
            if (!invoice.date_limite_paiement) return false
            if (['paid', 'cancelled'].includes(invoice.status)) return false
            return new Date(invoice.date_limite_paiement) < new Date()
        },

        statusLabel(s) {
            return { draft: 'Brouillon', issued: 'Document créé', sent: 'Envoyée', partial: 'Partiel', paid: 'Payée', cancelled: 'Annulée', accepted: 'Accepté', refused: 'Refusé' }[s] || s
        },

        statusClass(s) {
            return { draft: 'bg-gray-100 text-gray-500', issued: 'bg-blue-100 text-blue-600', sent: 'bg-indigo-100 text-indigo-600', partial: 'bg-orange-100 text-orange-600', paid: 'bg-green-100 text-green-600', cancelled: 'bg-red-100 text-red-500', accepted: 'bg-sky-100 text-sky-600', refused: 'bg-rose-100 text-rose-500' }[s] || 'bg-gray-100 text-gray-500'
        },

        statusDot(s) {
            return { draft: 'bg-gray-400', issued: 'bg-blue-500', sent: 'bg-indigo-500', partial: 'bg-orange-400', paid: 'bg-green-500', cancelled: 'bg-red-400' }[s] || 'bg-gray-400'
        },

        typeClass(t) {
            return { facture: 'bg-purple-100 text-purple-600', devis: 'bg-amber-100 text-amber-600', avoir: 'bg-rose-100 text-rose-600' }[t] || 'bg-gray-100 text-gray-500'
        },

        methodLabel(m) {
            return { virement: 'Virement', cheque: 'Chèque', especes: 'Espèces', cb: 'Carte bancaire', prelevement: 'Prélèvement', autre: 'Autre' }[m] || m
        },

        methodIcon(m) {
            return { virement: '🏦', cheque: '📝', especes: '💵', cb: '💳', prelevement: '🔄', autre: '💰' }[m] || '💰'
        },

        methodBg(m) {
            return { virement: 'bg-blue-50', cheque: 'bg-gray-50', especes: 'bg-green-50', cb: 'bg-purple-50', prelevement: 'bg-indigo-50', autre: 'bg-gray-50' }[m] || 'bg-gray-50'
        },

        paymentTypeLabel(t) {
            return { full: 'Solde', partial: 'Partiel', deposit: 'Acompte' }[t] || t
        },

        paymentTypeBadge(t) {
            return { full: 'bg-green-100 text-green-600', partial: 'bg-orange-100 text-orange-600', deposit: 'bg-blue-100 text-blue-600' }[t] || 'bg-gray-100 text-gray-500'
        },

        onInvoiceCreated(invoice) {
            this.closeModal()
            this.fetchInvoices()
            this.fetchOverdueCount()
        },

        async markAsDone(doc) {
            await this.withLoading(doc.id, () => this.changeStatus(doc, 'issued'))
        },

        async changeStatus(doc, newStatus) {
            try {
                this.loadingStatusChange = doc.id;
                await this.$request.post('/factures/change-status', {
                    invoice_id: doc.id,
                    statut: newStatus,
                });
                const idx = this.invoices.findIndex(f => f.id === doc.id);
                if (idx !== -1) this.$set(this.invoices[idx], 'status', newStatus);
                this.$toast.success('Statut mis à jour');
                this.fetchInvoices();
            } catch (e) {
                this.$toast.error('Erreur lors de la mise à jour du statut.');
            } finally {
                this.loadingStatusChange = null;
            }
        },

        async withLoading(invoiceId, fn) {
            this.loadingInvoiceId = invoiceId
            try {
                await fn()
            } finally {
                this.loadingInvoiceId = null
            }
        },

    },
}
</script>